import type { EventPricingConfig, MembershipTier } from "../types/pricingConfig";
import { MEMBERSHIP_TIER_LABELS, MEMBERSHIP_TIERS } from "../types/pricingConfig";
import type { AdminTicketType } from "../api/tickets";
import { emptyTicketSet, type TicketSetInput } from "../api/ticketSets";

/** Build editable ticket-set drafts from legacy pricingConfig + catalog tickets. */
export function draftsFromLegacyPricing(
  pricingConfig: EventPricingConfig,
  tickets: AdminTicketType[],
  eventStartDate: string,
): TicketSetInput[] {
  const sets: TicketSetInput[] = [
    {
      name: "Full Conference",
      hasPricingPeriods: true,
      periodConfig: {
        periods: [
          {
            key: "early_bird",
            label: "Early Bird",
            endsAt: pricingConfig.earlyBird.endsAt,
          },
          {
            key: "standard",
            label: "Standard",
            endsAt: pricingConfig.standard.endsAt,
          },
          {
            key: "on_site",
            label: "On-Site",
            endsAt: pricingConfig.onSite.endsAt,
          },
        ],
      },
      sortOrder: 0,
      types: MEMBERSHIP_TIERS.map((tier) => ({
        typeName: MEMBERSHIP_TIER_LABELS[tier],
        category: "full_conference" as const,
        membershipTier: tier,
        itemOrderedLabel: "Full Conference Registration",
        currency: "usd",
        pricesByPeriod: {
          early_bird: Math.round(
            pricingConfig.fullConferenceRatesUsd[tier].earlyBird * 100,
          ),
          standard: Math.round(
            pricingConfig.fullConferenceRatesUsd[tier].standard * 100,
          ),
          on_site: Math.round(
            pricingConfig.fullConferenceRatesUsd[tier].onSite * 100,
          ),
        },
      })),
    },
  ];

  const listeners = tickets.filter(
    (t) => t.status === "active" && t.category === "single_day",
  );
  if (listeners.length > 0) {
    sets.push({
      name: "Listener",
      hasPricingPeriods: false,
      periodConfig: null,
      sortOrder: 1,
      types: listeners.map((t) => ({
        typeName:
          (t.membershipTier &&
            MEMBERSHIP_TIER_LABELS[t.membershipTier as MembershipTier]) ||
          t.name,
        category: "single_day",
        membershipTier: (t.membershipTier as MembershipTier) ?? null,
        itemOrderedLabel: t.itemOrderedLabel ?? undefined,
        currency: t.currency,
        pricePence: t.pricePence,
      })),
    });
  }

  const addOns = tickets.filter(
    (t) =>
      t.status === "active" &&
      ["add_on", "extra_page", "additional_paper"].includes(t.category ?? ""),
  );
  const seenAddon = new Set<string>();
  const uniqueAddOns = addOns.filter((t) => {
    const key = t.category === "add_on" ? "add_on" : `${t.category}:${t.id}`;
    if (seenAddon.has(key)) return false;
    seenAddon.add(key);
    return true;
  });
  if (uniqueAddOns.length > 0) {
    sets.push({
      name: "Add-ons",
      hasPricingPeriods: false,
      periodConfig: null,
      sortOrder: 2,
      types: uniqueAddOns.map((t) => ({
        typeName:
          t.category === "add_on" ? "Banquet Ticket" : t.typeName || t.name,
        category: t.category as "add_on" | "extra_page" | "additional_paper",
        membershipTier: null,
        itemOrderedLabel: t.itemOrderedLabel ?? undefined,
        currency: t.currency,
        pricePence: t.pricePence,
      })),
    });
  }

  return sets.length > 0 ? sets : [emptyTicketSet(eventStartDate)];
}
